import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { UserView, VerifyEmailBody } from '@lemoncloud/codes-backend-api';
import { useMutation } from '@tanstack/react-query';

import { useCustomMutation } from '@codes/shared';

import { snsTestLogin, updateProfile, updateUserName, uploadImage, verifyEmail } from './apis';
import { SNS_TEST_LOGIN_KEY } from './consts';
import { HandleErrorEventFunc, HandleSuccessEventFunc, LemonRefreshTokenResult } from './types';

export const useUpdateUserPhoto = (
    handleSuccessEvent?: HandleSuccessEventFunc<UserView>,
    handleErrorEvent?: HandleErrorEventFunc
) =>
    useMutation({
        mutationKey: ['UPDATE_USER_PHOTO'],
        mutationFn: async ({ file, userId }: { file: File; userId: string }) => {
            const imageData = await uploadImage(file);
            const response = await updateProfile({ id: userId, photo: imageData?.url });
            return response;
        },
        onSuccess(data) {
            handleSuccessEvent && handleSuccessEvent(data);
        },
        onError(error) {
            handleErrorEvent && handleErrorEvent(error);
        },
    });

export const useUpdateProfile = (
    handleSuccessEvent?: HandleSuccessEventFunc<UserView>,
    handleErrorEvent?: HandleErrorEventFunc
) =>
    useMutation({
        mutationKey: ['UPDATE_USER_NAME'],
        mutationFn: updateUserName,
        onSuccess(data) {
            handleSuccessEvent && handleSuccessEvent(data);
        },
        onError(error) {
            handleErrorEvent && handleErrorEvent(error);
        },
    });

export const useSnsTestLoginQuery = (
    handleSuccessEvent?: HandleSuccessEventFunc<LemonRefreshTokenResult>,
    handleErrorEvent?: HandleErrorEventFunc
) =>
    useMutation({
        mutationKey: [SNS_TEST_LOGIN_KEY],
        mutationFn: snsTestLogin,
        onSuccess(data) {
            handleSuccessEvent && handleSuccessEvent(data);
        },
        onError(error) {
            handleErrorEvent && handleErrorEvent(error);
        },
    });

export const useVerifyEmail = () => {
    const mutation = useCustomMutation((body: VerifyEmailBody) => verifyEmail(body), {
        onError: error => {
            toast({ title: error instanceof Error ? error.message : 'An unknown error occurred' });
        },
    });

    return {
        ...mutation,
        isSending: mutation.isPending && mutation.variables?.step === 'send',
        isResending: mutation.isPending && mutation.variables?.step === 'resend',
        isChecking: mutation.isPending && mutation.variables?.step === 'check',
    };
};
